const State = cc.Enum({
    STATE_SPIN: 'main',
    STATE_SLAM_STOP: 'slamStop',
    STATE_DISABLE: 'disabled',
});

const Keyboard = require('PSKeyboardController');
const Events = require('PSEvents');
const PSState = require('PSState');

cc.Class({
    extends: cc.Component,

    properties: {
        disableBg: cc.Node,
        enableBg: cc.Node,
        timerSprite: cc.Sprite,
        iconSprite: cc.Sprite,
        idleIcon: cc.SpriteFrame,
        slamStopIcon: cc.SpriteFrame,
        icon: cc.Node,
    },

    onLoad () {
        // we should open 'fast-mode' popup, if user performs 4 consecutive slamStops
        // also we should open it once per session/game
        this._spinCountDown = 4; // decrease each time when spin performed
        this._slamCountDown = 4; // decrease each time when slam stop performed
        // check if the values are equally decreased, means the slam is consecutive

        this.widget = this.node.getComponent(cc.Widget);
        this.button = this.node.getComponent(cc.Button);
        this.icon.originScale = this.icon.scaleX;
        this.keyboardEnabled = true;

        Keyboard.registerHandler(['Space', 'Enter'], () => {
            if (this.keyboardEnabled) {
                this.onClick();
            }
        });
    },

    ctor () {
        this.hasTimer = false;
        this.isInteractable = true;
        this.isTimerComplete = true;
    },

    enableTimer (duration) {
        this.hasTimer = true;
        this.timerDuraiton = duration;
        this.lastTime = -1;
    },

    delayLock () {
        if (this.hasTimer) {
            this.isTimerComplete = false;
            this.timerSprite.node.active = true;
            this.timerSprite.fillRange = 1;

            this.lastTime = Date.now();
            
            if (this.intervalID) {
                clearInterval(this.intervalID);
            }
            this.intervalID = setInterval(() => {
                const now = Date.now();
                const dt = now - this.lastTime;

                this.lastTime = now;

                this.timerSprite.fillRange -= dt / (this.timerDuraiton * 1000);
                if (this.timerSprite.fillRange <= 0) {
                    this._onTimerComplete();
                    clearInterval(this.intervalID);
                    this.intervalID = null;
                }
            }, 30);
        }
    },

    setDefaultHandler () {
        this.customHandler = null;
        this.keyboardEnabled = true;
    },

    setCustomHandler (handler, keyboardEnabled = true) {
        this.customHandler = handler;
        this.setSlamStopState();
        this.keyboardEnabled = keyboardEnabled
    },

    _onTimerComplete () {
        this.isTimerComplete = true;
        this.timerSprite.node.active = false;
        this._changeState(this.state);
        this.node.emit('timerComplete');
    },

    access () {
        if (!this.hasTimer || this.isTimerComplete) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.node.once('timerComplete',() => {
               resolve(); 
            });
        });
    },

    _spinIcon (node, time, animEasing = cc.easeSineInOut(0.5)) {
        const s = node.originScale;

        node.stopAllActions();
        node.angle = 0;
        node.setScale(s,s);
        node.runAction(cc.spawn(
            cc.sequence(
                cc.scaleTo(time * 0.3, 1 * s, 1 * s).easing(cc.easeCircleActionOut()),
                cc.delayTime(time * 0.4),
                cc.scaleTo(time * 0.3, s, s).easing(cc.easeCircleActionIn())
            ),
            cc.rotateBy(time, -360).easing(animEasing)
        ));
    },

    _setSkin (skinState) {
        let newSkin;
        let isEnabledBg;

        switch (skinState) {
            case State.STATE_SPIN:
                newSkin = this.idleIcon;
                isEnabledBg = this.isTimerComplete;
                break;
            case State.STATE_DISABLE:
                isEnabledBg = false;
                newSkin = this.slamStopIcon;
                break;
            case State.STATE_SLAM_STOP:
                newSkin = this.slamStopIcon;
                isEnabledBg = true;
                break;
        }

        if (this.iconSprite.spriteFrame !== newSkin) {
            this._spinIcon(this.icon, 0.3, cc.easeSineInOut(0.8));
        }
        this.iconSprite.spriteFrame = newSkin;
        this.enableBg.active = isEnabledBg;
        this.disableBg.active = !isEnabledBg;

        this.button.interactable = isEnabledBg;
        this.node.opacity = isEnabledBg ? 255 : 100;
    },

    /**
     * Called on spin click or space/enter keydown. When it right state it will dispatch a spin
     * and show the according button animations.
     * @param skipAnimationOnSpin {Boolean=} if set to true button will skip spin animations and
     * just go directly to the appropriate state
     */
    onClick () {
        if (!this.isInteractable) { 
            return;
        }

        if (this.customHandler) {
            this._changeState(State.STATE_DISABLE);
            this.customHandler();
            return;
        }

        switch (this.state) {
            case State.STATE_SPIN:
                // if the timer is enabled and not finished yet, ignore the spin click
                if (!this.isTimerComplete) {
                    return;
                }
                // if not equal, means slam isn't performed, so we reset the values
                if (this._slamCountDown !== this._spinCountDown) {
                    this._slamCountDown = 4;
                    this._spinCountDown = 4;
                }
                this._spinCountDown--;
                this._dispatchSpin();
                if (PSState.quickSpinFeature) {
                    this._changeState(State.STATE_DISABLE);
                }
                break;
            case State.STATE_SLAM_STOP:
                this._slamCountDown--;

                if (this._slamCountDown === 0) {
                    this._dispatchFastModePopup();
                }
                this._dispatchSlamStop();
                break;
            case State.STATE_DISABLE:
                break;
        }
    },

    _changeState (newState) {
        this.state = newState;
        this._setSkin(this.state);
    },

    enableButton () {
        this._changeState(State.STATE_SPIN);
    },

    /**
     * Change this.isInteractable flag to ignore farther click response.
     * @param flag {Boolean}
     */
    setInteractable (flag) {
        this.isInteractable = flag;
    },

    /**
     * Switch to slam stop state directly.
     */
    setSlamStopState () {
        if (this.state === State.STATE_SLAM_STOP) {
            return;
        }
        this._changeState(State.STATE_SLAM_STOP);
    },

    /**
     * Set the state to disabled. The button changes its visual state,
     * whereas the hide function just changes only the this.isInteractable flag
     */
    disableButton () {
        if (this.state === State.STATE_DISABLE) {
            return;
        }
        this._changeState(State.STATE_DISABLE);
    },

    _dispatchFastModePopup () {
        const event = new cc.Event.EventCustom(Events.BUTTON_FAST_POPUP);

        this.node.dispatchEvent(event);
    },

    _dispatchSpin () {
        this._changeState(State.STATE_SLAM_STOP);
        const event = new cc.Event.EventCustom(Events.BUTTON_SPIN);

        this.node.dispatchEvent(event);
    },

    _dispatchSlamStop () {
        this._changeState(State.STATE_DISABLE);
        const event = new cc.Event.EventCustom(Events.BUTTON_SLAMSTOP);

        this.node.dispatchEvent(event);
    },

    show () {
        this.node.active = true;
    },

    hide () {
        this.node.active = false;
    }
});
